import React, { PropsWithChildren, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Modal } from '@swyftx/aviary/atoms/Modal';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { MarginRisk } from '@swyftx/margin/build/types';
import { AT_RISK_THRESHOLD, HEALTHY_THRESHOLD, MONITOR_THRESHOLD, WARNING_THRESHOLD } from '@swyftx/margin/build/utils';

import { Big } from '@shared/safe-big';

import { getMarginRiskColor } from 'src/lib/margin/utils';

import { MarginAccountHealthBar } from '../MarginAccountHealthBar';

type Props = {
  stepIncrement?: number;
  maxIncrement?: number;
};

const MarginAccountHealthModal: React.FC<PropsWithChildren<Props>> = ({ stepIncrement, maxIncrement, children }) => {
  const { t } = useTranslation('margin');

  const buildHealthLevel = useCallback(
    (
      riskLevel: MarginRisk,
      lowerThreshold: number | undefined,
      upperThreshold: number | undefined,
      marginLevel: Big,
    ) => (
      <>
        <Separator />

        <FlexLayout direction='column' spacing={4}>
          <Body color={getMarginRiskColor(marginLevel, marginLevel)}>
            {t(`accountHealth.riskRating.status.${riskLevel}`)}
          </Body>
          <Body size='small' weight='bold' color='primary'>
            {t('accountHealth.riskRating.marginLevel.title')}{' '}
            {t(`accountHealth.riskRating.marginLevel.${riskLevel}`, { lowerThreshold, upperThreshold })}
          </Body>
        </FlexLayout>

        <Body color='secondary'>{t(`accountHealth.riskRating.description.${riskLevel}`)}</Body>

        <MarginAccountHealthBar
          stepIncrement={stepIncrement}
          maxIncrement={maxIncrement}
          customMarginLevel={marginLevel}
          customRiskRating={riskLevel}
          fontSize='xxsmall'
        />
      </>
    ),
    [maxIncrement, stepIncrement, t],
  );

  return (
    <Modal
      triggerElement={children}
      title='Understanding account health'
      position='dynamic'
      className='md:w-[550px]'
      scrollClassName='md:max-h-[60vh]'
    >
      <FlexLayout direction='column' spacing={16} className='px-24 pb-24 '>
        <Body color='secondary'>{t('accountHealth.riskRating.title')}</Body>

        {buildHealthLevel(MarginRisk.StopOut, undefined, AT_RISK_THRESHOLD, Big(40))}
        {buildHealthLevel(MarginRisk.AtRisk, AT_RISK_THRESHOLD, WARNING_THRESHOLD, Big(60))}
        {buildHealthLevel(MarginRisk.Warning, WARNING_THRESHOLD, MONITOR_THRESHOLD, Big(90))}
        {buildHealthLevel(MarginRisk.Monitor, MONITOR_THRESHOLD, HEALTHY_THRESHOLD, Big(135))}
        {buildHealthLevel(MarginRisk.None, 0, 0, Big(0))}
      </FlexLayout>
    </Modal>
  );
};

export { MarginAccountHealthModal };
